// src/pages/Inscription/EditActeurModalByUser.jsx

import React, { useEffect, useState } from "react";
import {
  FaInfoCircle,
  FaSave,
  FaTimes,
  FaUserEdit,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../components/Button/Button";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import { updateActeur } from "../../services/acteursServices";

const EMPTY_ACTEUR = {
  nom: "",
  prenom: "",
  email: "",
  tel: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  date_naissance: "",
  club_acteur: "",
  photo_url: "",
  licence_number: "",
  groupe_souhaite: "",
};

const normalizeDateValue = (dateValue) => {
  if (!dateValue) return "";

  return String(dateValue).slice(0, 10);
};

const EditActeurModalByUser = ({
  isOpen,
  onClose,
  acteur,
  refreshList,
}) => {
  const [acteurData, setActeurData] = useState(EMPTY_ACTEUR);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!acteur) {
      setActeurData(EMPTY_ACTEUR);
      return;
    }

    setActeurData({
      nom: acteur.nom || "",
      prenom: acteur.prenom || "",
      email: acteur.email || "",
      tel: acteur.tel || "",
      adresse: acteur.adresse || "",
      cp: acteur.cp || "",
      commune: acteur.commune || "",
      genre: acteur.genre || "",
      date_naissance: normalizeDateValue(acteur.date_naissance),
      club_acteur: acteur.club_acteur || "",
      photo_url: acteur.photo_url || "",
      licence_number: acteur.licence_number || "",
      groupe_souhaite: acteur.groupe_souhaite || "",
    });
  }, [acteur]);

  /*
   * Empêche la page située derrière la modale de défiler.
   * Permet également de fermer la modale avec Échap.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setActeurData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!acteur?.id || isSubmitting) return;

    const finalActeurData = {
      ...acteurData,
      nom: acteurData.nom.trim(),
      prenom: acteurData.prenom.trim(),
      email: acteurData.email.trim(),
      tel: acteurData.tel.trim(),
      adresse: acteurData.adresse.trim(),
      cp: acteurData.cp.trim(),
      commune: acteurData.commune.trim(),
      club_acteur: acteurData.club_acteur.trim(),
      licence_number: acteurData.licence_number.trim(),
      groupe_souhaite: acteurData.groupe_souhaite.trim(),
      photo_url:
        acteurData.photo_url.trim() || "/img/user_avatar.jpg",
    };

    try {
      setIsSubmitting(true);

      await updateActeur(acteur.id, finalActeurData);

      toast.success("Acteur mis à jour avec succès");

      if (refreshList) {
        await refreshList();
      }

      onClose();
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de l'acteur :",
        error,
      );

      toast.error("Erreur lors de la mise à jour de l'acteur");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const inputClass = [
    "w-full",
    "min-w-0",
    "rounded-lg",
    "border",
    "border-slate-300",
    "bg-white/90",
    "px-3",
    "py-2.5",
    "text-sm",
    "text-slate-900",
    "outline-none",
    "transition",
    "placeholder:text-slate-400",
    "focus:border-primary/70",
    "focus:ring-2",
    "focus:ring-primary/60",
    "disabled:cursor-not-allowed",
    "disabled:opacity-60",
    "dark:border-slate-600",
    "dark:bg-slate-800/80",
    "dark:text-white",
    "dark:placeholder:text-slate-500",
  ].join(" ");

  const labelClass = [
    "mb-1.5",
    "block",
    "text-xs",
    "font-semibold",
    "uppercase",
    "tracking-wide",
    "text-slate-700",
    "dark:text-slate-200",
  ].join(" ");

  const sectionTitleClass = [
    "text-xs",
    "font-semibold",
    "uppercase",
    "tracking-[0.18em]",
    "text-slate-500",
    "dark:text-slate-300",
  ].join(" ");

  return (
    <div
      className={[
        "fixed",
        "inset-0",
        "z-50",
        "flex",
        "items-start",
        "justify-center",
        "overflow-y-auto",
        "bg-black/60",
        "p-2",
        "backdrop-blur-sm",
        "sm:items-center",
        "sm:p-4",
      ].join(" ")}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className={[
          "my-auto",
          "flex",
          "max-h-[calc(100dvh-1rem)]",
          "w-full",
          "max-w-2xl",
          "min-h-0",
          "flex-col",
          "overflow-hidden",
          "rounded-2xl",
          "border",
          "border-slate-200/80",
          "bg-white",
          "shadow-2xl",
          "dark:border-slate-700/80",
          "dark:bg-slate-900",
          "sm:max-h-[calc(100dvh-2rem)]",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-acteur-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="h-1.5 shrink-0 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

        {/* En-tête fixe */}
        <div
          className={[
            "flex",
            "shrink-0",
            "items-center",
            "justify-between",
            "gap-3",
            "border-b",
            "border-slate-200/80",
            "bg-white/95",
            "px-4",
            "py-3",
            "backdrop-blur",
            "dark:border-slate-700/80",
            "dark:bg-slate-900/95",
            "sm:px-5",
            "sm:py-4",
          ].join(" ")}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:flex">
              <FaUserEdit />
            </div>

            <div className="min-w-0">
              <h2
                id="edit-acteur-modal-title"
                className="truncate text-sm font-semibold uppercase tracking-[0.14em] text-primary sm:text-base"
              >
                Modifier un acteur
              </h2>

              <p className="mt-0.5 hidden text-xs text-slate-500 dark:text-slate-400 sm:block">
                Modifiez les informations concernant cet acteur.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={[
              "inline-flex",
              "h-9",
              "w-9",
              "shrink-0",
              "items-center",
              "justify-center",
              "rounded-full",
              "text-slate-600",
              "transition-colors",
              "hover:bg-slate-200/80",
              "disabled:cursor-not-allowed",
              "disabled:opacity-50",
              "dark:text-slate-200",
              "dark:hover:bg-slate-700/80",
            ].join(" ")}
            title="Fermer"
            aria-label="Fermer la fenêtre"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          {/* Zone défilable */}
          <div
            className={[
              "min-h-0",
              "flex-1",
              "overflow-x-hidden",
              "overflow-y-auto",
              "overscroll-contain",
              "px-4",
              "py-4",
              "sm:px-5",
              "sm:py-5",
            ].join(" ")}
          >
            <div className="space-y-5">
              {/* Identité */}
              <section className="space-y-3">
                <h3 className={sectionTitleClass}>Identité</h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label htmlFor="edit-acteur-nom" className={labelClass}>
                      Nom *
                    </label>

                    <input
                      id="edit-acteur-nom"
                      className={inputClass}
                      type="text"
                      name="nom"
                      value={acteurData.nom}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="family-name"
                      required
                    />
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-prenom"
                      className={labelClass}
                    >
                      Prénom *
                    </label>

                    <input
                      id="edit-acteur-prenom"
                      className={inputClass}
                      type="text"
                      name="prenom"
                      value={acteurData.prenom}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="given-name"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-genre"
                      className={labelClass}
                    >
                      Genre
                    </label>

                    <select
                      id="edit-acteur-genre"
                      className={inputClass}
                      name="genre"
                      value={acteurData.genre}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Sélectionnez</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>

                  <div className="min-w-0 sm:col-span-2">
                    <label
                      htmlFor="edit-acteur-date-naissance"
                      className={labelClass}
                    >
                      Date de naissance
                    </label>

                    <input
                      id="edit-acteur-date-naissance"
                      className={inputClass}
                      type="date"
                      name="date_naissance"
                      value={acteurData.date_naissance}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </section>

              {/* Coordonnées */}
              <section className="space-y-3">
                <h3 className={sectionTitleClass}>Coordonnées</h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-email"
                      className={labelClass}
                    >
                      Email
                    </label>

                    <input
                      id="edit-acteur-email"
                      className={inputClass}
                      type="email"
                      name="email"
                      value={acteurData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-tel"
                      className={labelClass}
                    >
                      Téléphone
                    </label>

                    <input
                      id="edit-acteur-tel"
                      className={inputClass}
                      type="tel"
                      name="tel"
                      value={acteurData.tel}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <label
                    htmlFor="edit-acteur-adresse"
                    className={labelClass}
                  >
                    Adresse
                  </label>

                  <input
                    id="edit-acteur-adresse"
                    className={inputClass}
                    type="text"
                    name="adresse"
                    value={acteurData.adresse}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    autoComplete="street-address"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-cp"
                      className={labelClass}
                    >
                      Code postal
                    </label>

                    <input
                      id="edit-acteur-cp"
                      className={inputClass}
                      type="text"
                      name="cp"
                      value={acteurData.cp}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="postal-code"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="min-w-0 sm:col-span-2">
                    <label
                      htmlFor="edit-acteur-commune"
                      className={labelClass}
                    >
                      Commune
                    </label>

                    <input
                      id="edit-acteur-commune"
                      className={inputClass}
                      type="text"
                      name="commune"
                      value={acteurData.commune}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="address-level2"
                    />
                  </div>
                </div>
              </section>

              {/* Club et informations Judo */}
              <section className="space-y-3">
                <h3 className={sectionTitleClass}>
                  Club & informations Judo
                </h3>

                <div className="min-w-0">
                  <label
                    htmlFor="edit-acteur-club"
                    className={labelClass}
                  >
                    Nom du club
                  </label>

                  <input
                    id="edit-acteur-club"
                    className={inputClass}
                    type="text"
                    name="club_acteur"
                    value={acteurData.club_acteur}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-licence"
                      className={labelClass}
                    >
                      Numéro de licence
                    </label>

                    <input
                      id="edit-acteur-licence"
                      className={inputClass}
                      type="text"
                      name="licence_number"
                      value={acteurData.licence_number}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="edit-acteur-photo"
                      className={labelClass}
                    >
                      URL de la photo
                    </label>

                    <input
                      id="edit-acteur-photo"
                      className={inputClass}
                      type="url"
                      name="photo_url"
                      value={acteurData.photo_url}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="https://..."
                      inputMode="url"
                    />
                  </div>
                </div>

                <InfoBubble title="Les différents groupes possibles">
                  <p>✅ Responsable de club</p>
                  <p>✅ Enseignant</p>
                  <p>✅ Membre de Commission</p>
                  <p>✅ Membre de Ligue</p>
                  <p>✅ Bénévolontaire</p>
                  <p>✅ Arbitre</p>
                  <p>✅ Commissaire Sportif</p>
                </InfoBubble>

                <div className="min-w-0">
                  <label
                    htmlFor="edit-acteur-groupe"
                    className={labelClass}
                  >
                    Choix de groupe(s)
                  </label>

                  <textarea
                    id="edit-acteur-groupe"
                    className={`${inputClass} min-h-[100px] resize-y`}
                    name="groupe_souhaite"
                    value={acteurData.groupe_souhaite}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    placeholder="Indiquez vos choix de groupe(s) ici (ex : Enseignant, Arbitre stagiaire, Membre Commission Arbitrage...)"
                  />
                </div>
              </section>

              <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200">
                <FaInfoCircle className="mt-0.5 shrink-0" />

                <p>
                  Vérifiez les informations avant d’enregistrer les
                  modifications.
                </p>
              </div>
            </div>
          </div>

          {/* Actions fixes en bas */}
          <div
            className={[
              "shrink-0",
              "border-t",
              "border-slate-200/80",
              "bg-white/95",
              "px-4",
              "py-3",
              "backdrop-blur",
              "dark:border-slate-700/80",
              "dark:bg-slate-900/95",
              "sm:px-5",
              "sm:py-4",
            ].join(" ")}
          >
            <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end sm:gap-3">
              <Button
                text="Fermer"
                icon={FaTimes}
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className={[
                  "w-full",
                  "justify-center",
                  "gap-2",
                  "border-none",
                  "bg-slate-200",
                  "text-slate-800",
                  "hover:bg-slate-300",
                  "dark:bg-slate-700",
                  "dark:text-white",
                  "dark:hover:bg-slate-600",
                  "sm:w-auto",
                ].join(" ")}
              />

              <Button
                text={
                  isSubmitting
                    ? "Mise à jour..."
                    : "Mettre à jour"
                }
                icon={FaSave}
                type="submit"
                disabled={isSubmitting}
                className="w-full justify-center gap-2 sm:w-auto"
              />
            </div>

            <p className="mt-2 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
              Les champs marqués d’un * sont obligatoires.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditActeurModalByUser;